import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, AfterViewInit, AfterContentInit, ViewChild, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ValueDescriptionPair } from 'src/app/core/controls/toolkit/select/value-description-pair.interface';
import { ReviewState } from 'src/app/review/models/review-state';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import { takeUntil, withLatestFrom, filter, tap, map, startWith, debounceTime, take } from 'rxjs/operators'
import { MatSelectChange } from '@angular/material/select';
import { SelectComponent } from 'src/app/core/controls/toolkit/select/select.component';
import { SignupService } from '../service/signup.service';
import { ProductType } from '../models/product-type';
import { FacadeService } from 'src/app/shared/service-facade/facade.service';
import { SettingsEnum } from 'src/app/shared/model/settings.enum';
 
@Component({
  selector: 'app-account-selector',
  templateUrl: './account-selector.component.html',
  styleUrls: ['./account-selector.component.scss']
})
export class AccountSelectorComponent implements OnInit, OnDestroy, AfterContentInit {
  isOrderChecks: string = '';
  isOrderDebit: string = '';
  @Input() set reviewState(value: ReviewState) {
    this.reviewState$.next(value)
  };
  @Input() accountListLabel: string = "";
  @Input() accountTypes: AccountType[] = [];
  @Input() accountName: string = "";
  @Input() accountAPY: string = "";
  @Input() accountAPYText: string = "";
  @Input() accountForm = SignupService.createAccountSelectionForm();
  @Input() error: string = "";
  @Input() typeEnabled = true;
  @Input() accountEnabled = true;
  @Input() canEdit = true;
  @Input() set isJoint(value: boolean) {this.isJoint$.next(value)};
  @Input() accountObservations = "";
  @Input() accountRestrictionsText = "";
  @Input() ratesFeesLink = "";
  @Input() canDelete = true;

  @Output() selectedAccountType$: EventEmitter<AccountType> = new EventEmitter();
  @Output() addedAccount$: EventEmitter<Account> = new EventEmitter();
  @Output() editedAccount$: EventEmitter<{previousAccount: string, newAccount: Account}> = new EventEmitter();
  @Output() cancelAccountSelection$: EventEmitter<AccountSelectorComponent> = new EventEmitter();
  @Output() deleteAccountSelection$: EventEmitter<AccountSelectorComponent> = new EventEmitter();

  @ViewChild("typeSelector") typeSelector!: SelectComponent;
  @ViewChild("accountSelector") accountSelector!: SelectComponent;

  private _reviewState: ReviewState | undefined;
  reviewState$: BehaviorSubject<ReviewState | undefined> = new BehaviorSubject<ReviewState | undefined>(undefined);
  reviewStates = ReviewState;
  destroySubject: Subject<void> = new Subject();
  cancelButtonClick$: Subject<void> = new Subject;

  protected readonly isJoint$ = new BehaviorSubject<boolean>(false);

  // Current form value on edit
  accountSnapshot!: any;

  get errors(): Array<string> {
    return this.error.split("|");
  };


  accountVerb$ = this.reviewState$.pipe(
    map(state => state === ReviewState.Initial
      ? "Add"
      : "Edit"
    )
  )

  actionVerb$ = this.reviewState$.pipe(
    map(state => state === ReviewState.Initial
      ? "Add Account"
      : "Save"
    )
  )

  cancelEdit$ = this.cancelButtonClick$.pipe(
    takeUntil(this.destroySubject),
    withLatestFrom(this.reviewState$),
    filter(([voidEvent, state]) => state === ReviewState.Edit)
  )

  cancelAccountSelectionEvent$ = this.cancelButtonClick$.pipe(
    takeUntil(this.destroySubject),
    withLatestFrom(this.reviewState$),
    filter(([ voidEvent, state ]) => state === ReviewState.Initial),
  ).subscribe(() => {
    this.cancelAccountSelection$.next(this);
  })

  canDelete$ = this.reviewState$.pipe(
    map(state => state === ReviewState.Edit && this.canDelete)
  );

  private _selectedType$: BehaviorSubject<AccountType | undefined> = new BehaviorSubject<AccountType | undefined>(undefined);
  accounts$ = this._selectedType$.pipe(
    filter(type => !!type),
    map(type => type!.accounts),
  );

  selectedAccount$: BehaviorSubject<Account | undefined> = new BehaviorSubject<Account | undefined>(undefined);

  productStripeColor: string | undefined | unknown;

  highlightClass$ = this.selectedAccount$.pipe(
    filter(account => !!account),
    tap(account =>
      this.productStripeColor = this._facade.productSettings.get(account!.value)?.get(SettingsEnum.ProductStripeColor)
    ),
    map(account =>
      !this.productStripeColor
      ? 'highlight-' + account!.productType?.toLowerCase()
      : ''
    )
  );


  accountFeatures$ = this.selectedAccount$.pipe(
    map(account => !!account
      ? account!.features
      : []
    )
  );

  canViewAccountOptions$ = this.selectedAccount$.pipe(
    map(value => value?.productType === ProductType.Checking)
  )

  canSave$!: Observable<boolean>;
  canShowDebitCardOptions$!: Observable<boolean>;

  canShowCheckOptions$ = combineLatest([
    this.isJoint$,
    this.accountForm.get("orderChecks")?.valueChanges as Observable<boolean>
  ]).pipe(
    map(([isJoint, orderDebitCard]) => isJoint && orderDebitCard)
  );

  get value(): SelectedAccount {
    return this.accountForm.value;
  }

  get hasError(): boolean {
    return !!this.error;
  }

  constructor(
    private _facade: FacadeService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this.initializeFormStreams();
  }

  ngOnInit(): void {
    this._selectedType$.pipe(
      takeUntil(this.destroySubject)
    ).subscribe(value => this.selectedAccountType$.emit(value));

    this.cancelEdit$.pipe(
      takeUntil(this.destroySubject)
    ).subscribe(() => {
      this.accountForm.patchValue(this.accountSnapshot);
      this.reviewState$.next(ReviewState.ReadOnly)
    });

    this.reviewState$.pipe(
      takeUntil(this.destroySubject)
    ).subscribe(value => {
      this._reviewState = value;
    });

    this.isJoint$.pipe(
      takeUntil(this.destroySubject)
    ).subscribe(value => {
      this.accountForm.get("isJoint")?.patchValue(value);
    })

    this._facade.isPWABrand$.pipe(
      take(1)
    ).subscribe(value => {
      if(value && this._facade.product) {
        const { productType }: any = this._facade.product;
        this.highlightClass$ = of(`highlight-${productType.toLowerCase()}`)
      }
    });
  }

  ngAfterContentInit(): void {
    this.reviewState$.next(this.reviewState);
  }

  ngOnDestroy(): void {
      this.destroySubject.next();
  }

  setAccountTypes(accountTypes: AccountType[]): void {
    this.accountTypes = JSON.parse(JSON.stringify(accountTypes));
    const selectedType = this.accountTypes.find(type => type.value === this._selectedType$.getValue()?.value);

    if (!selectedType?.accounts.find(account => account.value === this.selectedAccount$.getValue()?.value)) {
      selectedType?.accounts.push(this.selectedAccount$.getValue()!);
    }

    this._selectedType$.next(
      this.accountTypes.find(type => type.value === this._selectedType$.getValue()?.value)
    );

    this._changeDetectorRef.detectChanges();
  }

  initializeFormStreams(): void {
    this.canSave$ = this.accountForm.get("account")!.valueChanges.pipe(
      map(value => !!value)
    );

    const orderDebitCardFormControl = this.accountForm.get("orderDebitCard");
    this.canShowDebitCardOptions$ = combineLatest([
      this.isJoint$,
      (orderDebitCardFormControl!.valueChanges as Observable<boolean>).pipe(
        startWith(orderDebitCardFormControl!.value)
      )
    ]).pipe(
      map(([isJoint, orderDebitCard]) => isJoint && orderDebitCard)
    );
    this.checkProductSetting();  
  }


  checkProductSetting(){
    this.selectedAccount$.pipe(
      filter(account => !!account),debounceTime(300),
      tap(account =>
        {
          const accountValue = account!.value;
          const productSetting = this._facade.productSettings.get(accountValue);
          this.isOrderChecks =  productSetting?.get('orderCheck') as string;
          this.isOrderDebit =  productSetting?.get('orderDebit')as string;
      }
      ),     
    ).subscribe();   
    
  }

  cancel(): void {
    if (this._reviewState === ReviewState.Edit) {
      this.accountForm.patchValue(this.accountSnapshot);

      this._selectedType$.next(
        this.accountTypes.find(type => type.value === this.accountSnapshot.accountType)
      );

      setTimeout(() => {
        this.selectedAccount$.next(
          this._selectedType$.value?.accounts.find(account => account.value === this.accountSnapshot.account)
        );
      }, 0);
    }

    this.cancelButtonClick$.next();
  }

  add(): void {
    if (this._reviewState === ReviewState.Edit) {
      if (this.accountSnapshot.account !== this.selectedAccount$.value) {
        this.accountForm.get("applicationProductUID")?.setValue("");
      }

      this.editedAccount$.next({previousAccount: this.accountSnapshot.account, newAccount: this.selectedAccount$.value! });
    } else {
      this.addedAccount$.next(this.selectedAccount$.value!);
    }
    this.setReadOnly();
  }

  setReadOnly(): void {
    const { label, APY, code } = this.selectedAccount$.value!;

    this.accountName = label;
    this.accountAPY = APY;
    this.reviewState$.next(ReviewState.ReadOnly);
    this.accountForm.get("accountCode")?.setValue(code);
  }

  edit(): void {
    this.accountSnapshot = this.accountForm.value;
    this.accountForm.patchValue(this.accountForm.value);
    this.reviewState$.next(ReviewState.Edit);
  }

  delete(): void {
    this.reviewState$.next(ReviewState.ReadOnly)
    this.deleteAccountSelection$.next(this);
  }

  typeSelected(selection: MatSelectChange): void {
    this.accountForm.get("account")?.setValue("");
    this._selectedType$.next(
      this.accountTypes.find(type => type.value === selection.value)
    );
  }

  accountSelected(selection: MatSelectChange): void {
    this.selectedAccount$.next(
      this._selectedType$.value?.accounts.find(account => account.value === selection.value)
    );
    this.checkProductSetting();
  }

  setSelectedAccount(accountId: string, ): void {
    const accountType = this.accountTypes.find(type => type.accounts.find(account => account.value === accountId));
    if (accountType) {
      this._setAccountType(accountType);

      const account = accountType.accounts.find(account => account.value === accountId);
      if (account) { this._setAccount(account); }
    }
  }

  getEffectiveDate(): string {
    return SignupService.getEffectiveDate();
  }

  private _setAccountType(accountType: AccountType): void {
    this.accountForm.get("accountType")?.patchValue(accountType.value);
    this._selectedType$.next(accountType);
  }

  private _setAccount(account: Account): void {
    const accountControl = this.accountForm.get("account")!;
    this.selectedAccount$.next(account);
    accountControl.patchValue(account.value);
  }
}

export interface SelectedAccount {
  accountType: string,
  account: string
  accountCode: string,
  applicationProductUID: string,
  orderDebitCard: boolean,
  orderDebitCardForPrimary: boolean,
  orderDebitCardForCoApplicant: boolean,
  orderChecks: boolean,
  orderChecksForPrimary: boolean,
  orderChecksForCoApplicant: boolean,
  finished: boolean
}

export interface AccountType {
  label: string;
  value: string;
  accounts: Account[];
}

export interface Account {
  label: string;
  value: string;
  code: string;
  type: string;
  productType: ProductType;
  APY: string;
  APYText: string;
  allowJointApplications: boolean;
  observations: string;
  features: string[];
  TISA: string;
  maxNumberOfAccounts?: number;
}
